import { ChangeEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, ListItemText, Menu, MenuItem, Paper, Switch,
  TableBody, TableCell, TableContainer, TablePagination, TableRow, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import axios from "axios";
import { useDeleteQuote, useGetQuoteCustomers, useGetQuotes } from "../../api/useQuotes";
import { APP_ROUTES } from "../../constants/appRoutes";
import SearchBar from "../../submodule/components/SearchBar/SearchBar";
import EnhancedTableHead from "../../submodule/components/Tables/TableHead";
import { Order } from "../../submodule/components/Tables/Table";
import { TableStyled } from "../../submodule/components/Tables/TableStyles";
import { IQuote } from "../../shared/types";
import { resolveDynamicUrl } from "../../shared/utils";
import { filterQuotes, quoteDisplayStatus, quoteIsDeletable, quoteListCsv, quoteListErrorMessage, QuoteSortKey, recentActivity } from "./quoteListUtils";
import "./QuoteListPage.scss";
import { clearQuoteDraft, listQuoteDrafts, QuoteDraftSummary } from "../../shared/quoteDraft";
import DraftsDialog from "../../components/DraftsDialog/DraftsDialog";
import RestoreDraftPrompt from "../../components/RestoreDraftPrompt/RestoreDraftPrompt";

const headCells = [
  { id: "quote_id", label: "#", numeric: false, disablePadding: true },
  { id: "name", label: "Quote", numeric: false, disablePadding: true },
  { id: "display_name", label: "Customer", numeric: false, disablePadding: true },
  { id: "status", label: "Status", numeric: false, disablePadding: true },
  { id: "recent_activity", label: "Changed", numeric: false, disablePadding: true },
  { id: "actions", label: "Actions", numeric: false, disablePadding: true, sortable: false },
];

const formatDate = (value: string | null) =>
  value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value)) : "-";

const valueForSort = (quote: IQuote, key: QuoteSortKey) => {
  if (key === "recent_activity") return recentActivity(quote);
  return quote[key] ?? "";
};

const QuoteListPage = () => {
  const navigate = useNavigate();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [myQuotes, setMyQuotes] = useState(false);
  const [quoteType, setQuoteType] = useState<"all" | "customer" | "prospect">("all");
  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<QuoteSortKey>("recent_activity");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [newQuoteAnchor, setNewQuoteAnchor] = useState<HTMLElement | null>(null);
  // Every local (never-sent-to-the-server) draft, both new-quote drafts and in-progress edits of
  // existing quotes. Re-read after any Restore/Delete/Clear All so the dialog and badge stay live.
  const [drafts, setDrafts] = useState<QuoteDraftSummary[]>(() => listQuoteDrafts());
  const [autoPopupOpen, setAutoPopupOpen] = useState(drafts.length > 0);
  const [draftsDialogOpen, setDraftsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IQuote | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Issue: Copy and Delete were two separate icon buttons sitting right next to each other in the
  // same cell - easy to fat-finger the wrong one, especially on Delete (destructive). One overflow
  // menu per row, same pattern as the line-item grid's own Actions column.
  const [rowMenu, setRowMenu] = useState<{ quote: IQuote; anchor: HTMLElement } | null>(null);
  const quotes = useGetQuotes(includeArchived);
  const organizations = useGetQuoteCustomers();
  const deleteQuote = useDeleteQuote();
  // useGetQuotes sets placeholderData: [], so react-query reports a successful (not loading) query
  // while the first fetch for a key is still in flight - isPlaceholderData, not isLoading, is what
  // means "nothing real to show yet". Also true after toggling Show Archived, which switches to a
  // different, not-yet-cached query key.
  const quotesLoading = !quotes.isError && (quotes.isLoading || quotes.isPlaceholderData);
  const loadError = quotes.isError ? quoteListErrorMessage(quotes.error, process.env.REACT_APP_ADMIN_API_BASE) : "";

  const refreshDrafts = () => setDrafts(listQuoteDrafts());
  // Who/what a draft is for - a new customer quote's customerGuid resolved against the customer
  // list, a new prospect quote's own typed name, or (an existing-quote edit) that quote's own
  // reference and name.
  const resolveDraftSubject = (draft: QuoteDraftSummary): string => {
    if (draft.quoteId) {
      const existing = (quotes.data || []).find(item => String(item.quote_id) === draft.quoteId);
      return existing ? `CSP-CP-${existing.quote_id} - ${existing.display_name}` : `CSP-CP-${draft.quoteId}`;
    }
    if (draft.quoteType === "prospect") return draft.prospectName || "New prospect quote";
    const customer = (organizations.data || []).find(item => item.customer_guid === draft.customerGuid);
    return customer ? customer.display_name : "New customer quote";
  };
  const closeAutoPopup = () => setAutoPopupOpen(false);
  const restoreDraft = (draft: QuoteDraftSummary) => {
    closeAutoPopup(); setDraftsDialogOpen(false);
    navigate(draft.quoteId
      ? `${resolveDynamicUrl(APP_ROUTES.QUOTE_DETAIL, { quoteId: draft.quoteId })}?restoreDraft=1`
      : `${draft.quoteType === "prospect" ? APP_ROUTES.NEW_PROSPECT_QUOTE : APP_ROUTES.NEW_CUSTOMER_QUOTE}?restoreDraft=1&draftId=${encodeURIComponent(draft.draftId)}`);
  };
  const deleteDraft = (draft: QuoteDraftSummary) => {
    clearQuoteDraft(draft.quoteType, draft.draftId);
    refreshDrafts();
  };
  const clearAllDrafts = () => {
    drafts.forEach(draft => clearQuoteDraft(draft.quoteType, draft.draftId));
    setDrafts([]);
  };

  const data = useMemo(() => {
    const filtered = filterQuotes(quotes.data || [], searchText, myQuotes, quoteType);
    return [...filtered].sort((a, b) => {
      const left = valueForSort(a, orderBy);
      const right = valueForSort(b, orderBy);
      const comparison = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
      return order === "asc" ? comparison : -comparison;
    });
  }, [quotes.data, searchText, myQuotes, quoteType, order, orderBy]);

  const changeFilter = (setter: (value: boolean) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    setter(event.target.checked);
    setPage(0);
  };

  const sort = (_event: React.MouseEvent<unknown>, property: QuoteSortKey) => {
    setOrder(orderBy === property && order === "desc" ? "asc" : "desc");
    setOrderBy(property);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    deleteQuote.mutate(String(deleteTarget.quote_id), {
      onSuccess: () => setDeleteTarget(null),
      onError: error => setDeleteError(axios.isAxiosError(error) && typeof error.response?.data === "string"
        ? error.response.data : error instanceof Error ? error.message : String(error)),
    });
  };

  const downloadList = () => {
    const url = URL.createObjectURL(new Blob([quoteListCsv(data)], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "quotes.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="quote-list-page">
      <RestoreDraftPrompt open={autoPopupOpen && drafts.length > 0} title="Restore unsaved quote?"
        onClose={closeAutoPopup} onNotNow={closeAutoPopup}
        onDiscard={() => { if (drafts[0]) deleteDraft(drafts[0]); closeAutoPopup(); }}
        onRestore={() => drafts[0] && restoreDraft(drafts[0])}>
        A local draft for {drafts[0] ? resolveDraftSubject(drafts[0]) : ""} from {drafts[0] ? new Date(drafts[0].updatedAt).toLocaleString() : ""} was found.
        {drafts.length > 1 && ` (${drafts.length - 1} more local draft${drafts.length > 2 ? "s" : ""} available under Drafts.)`}
      </RestoreDraftPrompt>
      <DraftsDialog open={draftsDialogOpen} drafts={drafts} resolveSubject={resolveDraftSubject}
        onClose={() => setDraftsDialogOpen(false)} onRestore={restoreDraft} onDelete={deleteDraft} onClearAll={clearAllDrafts} />
      <Dialog open={deleteTarget !== null} onClose={() => (deleteQuote.isLoading ? undefined : setDeleteTarget(null))} fullWidth maxWidth="sm">
        <DialogTitle>Delete CSP-CP-{deleteTarget?.quote_id}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>This permanently removes {deleteTarget?.name || "this quote"} from the quote list. This cannot be undone from here.</Typography>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" variant="outlined" disabled={deleteQuote.isLoading} onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteOutlineIcon />} disabled={deleteQuote.isLoading} onClick={confirmDelete}>{deleteQuote.isLoading ? "Deleting…" : "Delete"}</Button>
        </DialogActions>
      </Dialog>
      <Box className="quote-list-toolbar">
        <SearchBar value={searchText} onChange={(event: ChangeEvent<HTMLInputElement>) => { setSearchText(event.target.value); setPage(0); }} />
        <Box className="quote-list-toolbar-actions">
          <Tooltip title="Download the currently filtered and sorted quote list as CSV." enterDelay={1800} enterNextDelay={1800}>
            <Button className="btn-rounded quote-list-action-button" variant="contained" startIcon={<DownloadOutlinedIcon />}
              disabled={!data.length} onClick={downloadList}>CSV</Button>
          </Tooltip>
          {drafts.length > 0 && <Tooltip title="View and restore local quote drafts." enterDelay={1800} enterNextDelay={1800}>
            <Badge badgeContent={drafts.length} color="primary" overlap="rectangular">
              <Button className="btn-rounded quote-list-action-button" variant="outlined" onClick={() => setDraftsDialogOpen(true)}>Drafts</Button>
            </Badge>
          </Tooltip>}
          <Tooltip title="Create a new customer or prospect quote." enterDelay={1800} enterNextDelay={1800}>
            <Button className="btn-rounded quote-list-action-button quote-new-button" variant="contained" endIcon={<ArrowDropDownIcon />}
              onClick={event => setNewQuoteAnchor(event.currentTarget)}>New</Button>
          </Tooltip>
          <Menu anchorEl={newQuoteAnchor} open={Boolean(newQuoteAnchor)} onClose={() => setNewQuoteAnchor(null)}>
            <MenuItem onClick={() => navigate(APP_ROUTES.NEW_CUSTOMER_QUOTE)}>Customer Quote</MenuItem>
            <MenuItem onClick={() => navigate(APP_ROUTES.NEW_PROSPECT_QUOTE)}>Prospect Quote</MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box className="quote-list-filters">
        <FormControlLabel control={<Switch checked={myQuotes} onChange={changeFilter(setMyQuotes)} />} label="My Quotes" />
        <FormControlLabel control={<Switch checked={includeArchived} onChange={changeFilter(setIncludeArchived)} />} label="Show Archived / Voided" />
        <ToggleButtonGroup className="quote-type-filter" size="small" exclusive value={quoteType} aria-label="Quote type"
          onChange={(_event, value) => { if (value) { setQuoteType(value); setPage(0); } }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="customer">Customers</ToggleButton>
          <ToggleButton value="prospect">Prospects</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {quotes.isError && <Box className="quote-list-message">
        <Typography color="error">Quotes could not be loaded. {loadError}</Typography>
        <Button size="small" onClick={() => navigator.clipboard.writeText(loadError)}>Copy Error</Button>
      </Box>}
      {quotesLoading && <Box className="quote-list-loading" role="status" aria-live="polite">
        <CircularProgress color="primary" size="2rem" />
        <Typography>Loading quotes...</Typography>
      </Box>}
      {!quotes.isError && !quotesLoading && !data.length && <Typography className="quote-list-message" variant="h6">No quotes found</Typography>}
      {!quotesLoading && !!data.length && (
        <Paper sx={{ boxShadow: "none" }}>
          <TableContainer className="quote-table-container">
            <TableStyled stickyHeader aria-label="quotes table">
              <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={sort} rowCount={data.length} headCells={headCells} />
              <TableBody>
                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((quote) => (
                  <TableRow hover key={quote.quote_id} tabIndex={0} className={quote.status.toLowerCase() === "void" ? "quote-row-void quote-clickable-row" : "quote-clickable-row"}
                    onClick={() => navigate(resolveDynamicUrl(APP_ROUTES.QUOTE_DETAIL, { quoteId: String(quote.quote_id) }))}
                    onKeyDown={event => { if (event.key === "Enter" || event.key === " ") navigate(resolveDynamicUrl(APP_ROUTES.QUOTE_DETAIL, { quoteId: String(quote.quote_id) })); }}>
                    <TableCell>{quote.quote_id}</TableCell>
                    <TableCell>{quote.name || "Untitled quote"}</TableCell>
                    <TableCell>{quote.display_name}</TableCell>
                    <TableCell><Chip size="small" label={quoteDisplayStatus(quote)} className={`quote-status quote-status-${quoteDisplayStatus(quote).toLowerCase()}`} /></TableCell>
                    <TableCell>{quote.modified_by_display_name || quote.created_by_display_name}<small>{formatDate(quote.modified_at || quote.created_at)}</small></TableCell>
                    <TableCell>
                      <IconButton aria-label={`Actions for CSP-CP-${quote.quote_id}`} aria-haspopup="true" aria-expanded={rowMenu?.quote.quote_id === quote.quote_id}
                        onKeyDown={event => event.stopPropagation()}
                        onClick={event => { event.stopPropagation(); setRowMenu({ quote, anchor: event.currentTarget }); }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableStyled>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]} component="div" count={data.length}
            rowsPerPage={rowsPerPage} page={Math.min(page, Math.max(0, Math.ceil(data.length / rowsPerPage) - 1))}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}
      <Menu anchorEl={rowMenu?.anchor ?? null} open={rowMenu !== null} onClose={() => setRowMenu(null)}>
        {rowMenu && [
          <MenuItem key="copy" onClick={() => { navigate(resolveDynamicUrl(APP_ROUTES.COPY_QUOTE, { quoteId: String(rowMenu.quote.quote_id) })); setRowMenu(null); }}>
            <ContentCopyOutlinedIcon fontSize="small" /><ListItemText primary="Copy to New Quote" />
          </MenuItem>,
          ...(quoteIsDeletable(rowMenu.quote) ? [
            <MenuItem key="delete" onClick={() => { setDeleteError(null); setDeleteTarget(rowMenu.quote); setRowMenu(null); }}>
              <DeleteOutlineIcon fontSize="small" /><ListItemText primary="Delete" />
            </MenuItem>,
          ] : []),
        ]}
      </Menu>
    </div>
  );
};

export default QuoteListPage;
